// cartState.ts

import { makeVar } from '@apollo/client'

import { Currency } from '../types/currency'
import { SearchAvailabilityInput } from '../types/searchAvailability'

export const totalQuantityVar = makeVar<number>(
  Number(localStorage.getItem('totalQuantity')) || 0
)
export type CartDetails = {
  propertyCode: string
  currrncy: Currency
  arrival: string
  departure: string
  guestsAges: number[]
  providerCodes: string[]
  channelId: string
}

export const cartDetailsForSearch = (
  search: SearchAvailabilityInput
): CartDetails => ({
  propertyCode: search.propertyCode || '',
  currrncy: search.currency,
  arrival: search.arrival,
  departure: search.departure,
  guestsAges: [...search.guestsAges].sort((left, right) => left - right),
  providerCodes: [...(search.providerCodes || [])].sort(),
  channelId: search.channelId
})

const sameValues = <T>(left: T[] | undefined, right: T[]): boolean =>
  Array.isArray(left) &&
  left.length === right.length &&
  left.every((value, index) => value === right[index])

export const cartMatchesSearch = (
  cart: Partial<CartDetails> | null,
  search: SearchAvailabilityInput
): boolean => {
  if (!cart) return false

  const expected = cartDetailsForSearch(search)
  return (
    cart.propertyCode === expected.propertyCode &&
    cart.currrncy === expected.currrncy &&
    cart.arrival === expected.arrival &&
    cart.departure === expected.departure &&
    cart.channelId === expected.channelId &&
    sameValues(cart.guestsAges, expected.guestsAges) &&
    sameValues(cart.providerCodes, expected.providerCodes)
  )
}

export const parseCartDetails = (
  serialized: string | null
): Partial<CartDetails> | null => {
  if (!serialized) return null

  try {
    const parsed: unknown = JSON.parse(serialized)
    return parsed && typeof parsed === 'object'
      ? (parsed as Partial<CartDetails>)
      : null
  } catch {
    return null
  }
}

type ActiveCartResponseInput = {
  requestGeneration: number
  activeGeneration: number
  requestedCartID: string
  activeCartID: string | null
  storedCartDetails: Partial<CartDetails> | null
  search: SearchAvailabilityInput
}

export const isActiveCartResponse = ({
  requestGeneration,
  activeGeneration,
  requestedCartID,
  activeCartID,
  storedCartDetails,
  search
}: ActiveCartResponseInput): boolean =>
  requestGeneration === activeGeneration &&
  requestedCartID !== '' &&
  requestedCartID === activeCartID &&
  cartMatchesSearch(storedCartDetails, search)

type ActiveCartCreationInput = {
  requestGeneration: number
  activeGeneration: number
  requestedCartDetails: CartDetails
  search: SearchAvailabilityInput
}

export const isActiveCartCreation = ({
  requestGeneration,
  activeGeneration,
  requestedCartDetails,
  search
}: ActiveCartCreationInput): boolean =>
  requestGeneration === activeGeneration &&
  cartMatchesSearch(requestedCartDetails, search)

export const cartDetailsVar = makeVar<CartDetails>({
  propertyCode: '',
  currrncy: '',
  arrival: '',
  departure: '',
  guestsAges: [],
  providerCodes: [],
  channelId: ''
})
